/**
 * productSpecs.ts — Typed API client for Product Specs Admin routes
 * Mirrors backend/src/modules/admin/product-specs-admin.routes.ts
 *
 * All paths are under /api/v1/admin/product-specs/…
 * Uses the same `api` axios instance as proposalBuilder.ts (auth headers injected
 * by the interceptor in frontend/src/utils/api.ts).
 *
 * Architecture: each product family's spec DATA is stored as one JSON blob that
 * mirrors the SS_PRODUCT_SPECS shape. Editing the blob flows into proposals via
 * the resolver. The editor opens on DB-or-code data; `edited:false` means the
 * family is still served from the code constant (no DB override yet).
 */

import { api } from '../utils/api';

// ─── Types ───────────────────────────────────────────────────────────────────

/** The 5 editor shapes, one per family layout. Frozen by Squad A's contract. */
export type EditorShape =
  | 'tank_variant'
  | 'hp_model'
  | 'swh_series'
  | 'fixed_page'
  | 'datasheet_list';

/** Family key — must match the backend registry (PRODUCT_SPEC_FAMILIES). */
export type FamilyKey =
  | 'SS_WATER_TANK'
  | 'HOT_WATER_TANK'
  | 'DOMESTIC_HEAT_PUMP'
  | 'COMMERCIAL_HEAT_PUMP'
  | 'POOL_HEAT_PUMP'
  | 'R290'
  | 'EVI'
  | 'SOLAR_WATER_HEATER'
  | 'STAINLESS_STEEL_PLUMBING'
  | 'HYBRID'
  | 'HOT_WATER_TANK_DATASHEETS';

/** One row in the family list (left rail). */
export interface SpecFamily {
  key: FamilyKey;
  displayName: string;
  shape: EditorShape;
  /** true = a DB override row exists (served from DB); false = code default. */
  edited: boolean;
  /** count of variants / models / datasheet SKUs in the resolved family. */
  modelCount: number;
}

export interface FamiliesResponse {
  families: SpecFamily[];
}

/** The resolved family data (DB or code) plus its meta, for the editor form. */
export interface FamilyDetail {
  familyKey: FamilyKey;
  shape: EditorShape;
  edited: boolean;
  /** The exact SS_PRODUCT_SPECS[familyKey] shape (or the HWT datasheet Record). */
  data: any;
}

// ─── API calls ───────────────────────────────────────────────────────────────

/**
 * GET /admin/product-specs
 * Returns every spec family with its editor shape, edited flag, and model count.
 */
export async function listFamilies(): Promise<SpecFamily[]> {
  const res = await api.get<FamiliesResponse>('/admin/product-specs');
  return res.data?.families ?? [];
}

/**
 * GET /admin/product-specs/:familyKey
 * Returns the resolved family data (DB override when present, else code default).
 */
export async function getFamily(familyKey: FamilyKey | string): Promise<FamilyDetail> {
  const res = await api.get<FamilyDetail>(
    `/admin/product-specs/${encodeURIComponent(familyKey)}`,
  );
  return res.data;
}

/**
 * PUT /admin/product-specs/:familyKey  body { data }
 * Upsert the family override. Returns the persisted `{ data }`.
 */
export async function putFamily(
  familyKey: FamilyKey | string,
  data: any,
): Promise<{ data: any }> {
  const res = await api.put<{ data: any }>(
    `/admin/product-specs/${encodeURIComponent(familyKey)}`,
    { data },
  );
  return res.data;
}

/**
 * DELETE /admin/product-specs/:familyKey
 * Reset to code default (delete the DB override row). Returns `{ reset: true }`.
 */
export async function resetFamily(
  familyKey: FamilyKey | string,
): Promise<{ reset: boolean }> {
  const res = await api.delete<{ reset: boolean }>(
    `/admin/product-specs/${encodeURIComponent(familyKey)}`,
  );
  return res.data;
}

/**
 * POST /admin/product-specs/:familyKey/preview  body { sku, data }
 * Resolves the given SKU/variant against the DRAFT `data` and returns the
 * rendered spec-sheet HTML — render in a sandboxed iframe.
 */
export async function previewFamily(
  familyKey: FamilyKey | string,
  sku: string,
  data: any,
): Promise<{ html: string }> {
  const res = await api.post<{ html: string }>(
    `/admin/product-specs/${encodeURIComponent(familyKey)}/preview`,
    { sku, data },
  );
  return res.data;
}
